/**
 * Reusable style objects for CompSheet and similar data table views.
 * Import these to avoid repeating sx prop definitions.
 */

import { WORKSPACE_CONTENT_MAX_WIDTH } from '../components/common/AnalyticsWorkspace/workspaceStyles';

// ============================================
// Container Styles
// ============================================

/**
 * Main page container style
 */
export const pageContainerSx = {
  height: '100%',
  marginBottom: '0px',
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Header container with shadow
 * @param {Object} theme - MUI theme object
 * @returns {Object} sx prop object
 */
export const headerContainerSx = (theme) => ({
  backgroundColor: theme.background.backgroundColor,
  marginBottom: '12px',
  borderTopLeftRadius: '0px',
  borderTopRightRadius: '0px',
  boxShadow: theme.palette.mode === 'light'
    ? '0px 1px 1px -1px rgba(0,0,0,0.06), 0px 1px 1px 0px rgba(0,0,0,0.03), 0px 3px 3px 0px rgba(0,0,0,0.1)'
    : '0px 1px 1px -1px rgba(0,0,0,0.4), 0px 1px 1px 0px rgba(0,0,0,0.1), 0px 3px 3px 0px rgba(0,0,0,0.3)',
});

/**
 * Content container centered
 */
export const contentContainerSx = {
  flex: 1,
  width: '100%',
  maxWidth: WORKSPACE_CONTENT_MAX_WIDTH,
  mx: 'auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

/**
 * Loading container centered
 */
export const loadingContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
};

// ============================================
// Filter Bar Styles
// ============================================

/**
 * Filter bar outer container
 */
export const filterBarContainerSx = {
  display: 'flex',
  width: '1400px',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

/**
 * Filter bar inner wrapper
 */
export const filterBarWrapperSx = {
  border: '0px solid lightgrey',
  width: '100%',
  maxWidth: '1400px',
  marginLeft: '4px',
  paddingTop: '2px',
  marginTop: '0px',
  paddingLeft: '4px',
  paddingRight: '4px',
  paddingBottom: '4px',
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
};

/**
 * Filter label style
 * @param {Object} theme - MUI theme object
 * @param {boolean} isMobile - Is mobile viewport
 * @returns {Object} sx prop object
 */
export const filterLabelSx = (theme, isMobile) => ({
  maxWidth: isMobile ? '60px' : '55px',
  fontSize: '11px',
  textAlign: 'left',
  paddingBottom: '0px',
  paddingTop: '0px',
  paddingRight: '12px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
  minHeight: '30px',
  whiteSpace: 'normal',
  lineHeight: '1.3',
});

/**
 * Filter item wrapper style
 */
export const filterItemSx = {
  marginTop: '0px',
  marginBottom: '0px',
  marginLeft: '2px',
  marginRight: '2px',
  flexShrink: 1,
  display: 'flex',
  alignItems: 'flex-start',
  minHeight: '30px',
};

/**
 * Filter item wrapper with custom width
 * @param {number|string} width - Width value
 * @returns {Object} sx prop object
 */
export const filterItemWithWidthSx = (width) => ({
  ...filterItemSx,
  width,
});

// ============================================
// Toggle/Switch Styles
// ============================================

/**
 * Stats toggle container style
 * @param {Object} theme - MUI theme object
 * @returns {Object} sx prop object
 */
export const statsToggleContainerSx = (theme) => ({
  padding: '2px',
  backgroundColor: theme.tableHeader.highlight,
  width: '125px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  border: theme.palette.mode === 'light'
    ? `1px solid ${theme.palette.grey[400]}`
    : `1px solid ${theme.palette.grey[700]}`,
  borderRadius: '4px',
});

/**
 * Stats toggle label style
 * @param {Object} theme - MUI theme object
 * @returns {Object} sx prop object
 */
export const statsToggleLabelSx = (theme) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  paddingLeft: '6px',
  color: theme.palette.text.secondary,
  fontSize: '9px',
});

// ============================================
// Table Header Styles
// ============================================

/**
 * Table title container style
 * @param {Object} theme - MUI theme object
 * @param {number} tableWidth - Calculated table width
 * @param {boolean} isMobile - Is mobile viewport
 * @returns {Object} sx prop object
 */
export const tableTitleContainerSx = (theme, tableWidth, isMobile) => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: isMobile ? 'wrap' : 'nowrap',
  paddingBottom: '4px',
  width: tableWidth,
  maxWidth: isMobile ? '98%' : '90%',
  color: theme.palette.primary.main,
  fontSize: '20px',
  fontWeight: '300',
  textAlign: 'left',
  paddingLeft: '0.5%',
});

/**
 * Methodology link style
 * @param {Object} theme - MUI theme object
 * @returns {Object} sx prop object
 */
export const methodologyLinkSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 400,
  marginTop: '2px',
  marginLeft: 1,
});

/**
 * Methodology link text style
 * @param {Object} theme - MUI theme object
 * @returns {Object} sx prop object
 */
export const methodologyLinkTextSx = (theme) => ({
  fontWeight: 'light',
  fontStyle: 'italic',
  fontSize: '10px',
  color: theme.palette.text.secondary,
});

// ============================================
// Collapse Toggle Styles
// ============================================

/**
 * Collapse toggle button style
 */
export const collapseToggleSx = {
  p: 0.1,
  fontSize: 15,
  marginTop: '2px',
  marginRight: '2px',
};

// ============================================
// Utility Functions
// ============================================

/**
 * Calculate table width from column definitions
 * @param {Array} colDefs - Column definitions array
 * @param {number} minWidth - Minimum table width (default 500)
 * @returns {number} Calculated width
 */
export const calculateTableWidth = (colDefs, minWidth = 500) => {
  const calculatedWidth = colDefs.reduce((sum, colDef) => sum + (colDef.width || 100), 0) + 5;
  return Math.max(calculatedWidth, minWidth);
};

/**
 * Calculate table height based on row count
 * @param {number} rowCount - Number of data rows
 * @param {boolean} showStats - Whether stats rows are shown
 * @param {number} rowHeight - Height per row (default 30)
 * @returns {number} Calculated height
 */
export const calculateTableHeight = (rowCount, showStats, rowHeight = 24) => {
  if (rowCount === 0) return 75;
  const statsHeight = showStats ? 56 : 0;
  return ((rowCount + 2) * rowHeight) + statsHeight;
};

// ============================================
// Filter Styles
// ============================================

/**
 * Sublabel style for filter sections
 * Used below filter components to indicate their purpose
 * @param {Object} theme - MUI theme object
 * @returns {Object} sx prop object
 *
 * NOTE: This is a legacy export for backwards compatibility.
 * New code should import filterSublabelSx from '../styles' (common.js)
 */
export const filterSublabelSx = (theme) => ({
  fontSize: '10px',
  color: theme.palette.grey[500],
  fontWeight: 400,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  pt: 0.5,
  textAlign: 'center',
});
